/** A place for random useful utilities */
import React from 'react';

const NUTRIENT_MULTIPLIERS = {
  FAT: 9,
  PROTEIN: 4,
  CARBS: 4,
};

const DAILY_MEALS = [
  'Coffee',
  'Breakfast',
  'Snack1',
  'Lunch',
  'Snack2',
  'Dinner',
  'Dessert',
];

const NUTRIENTS = ['calories', 'protein', 'fat', 'carbohydrates', 'fiber'];

const NUTRIENT_TOLERANCES = {
  calories: { tolerance: { min: 40, max: 40 }, unit: 'cal' },
  protein: { tolerance: { min: 20, max: 20 }, unit: 'g' },
  fat: { tolerance: { min: 20, max: 35 }, unit: '%' },
  fiber: { tolerance: { min: 10, max: 10 }, floor: 15, unit: 'g' },
};

/**
 * nl2br
 * Replaces newlines in text with a BR tag
 * thanks to: https://codesandbox.io/s/94k7k80jxy
 */
const nl2br = (text) => {
  return text
    ? text.split(/\r\n|\r|\n/).reduce((res, frag, i, arr) => {
        // Because React even cares that newlines have unique keys ¯\_(ツ)_/¯
        const br = React.createElement('br', { key: `line-break-${i}` });
        return [...res, frag, i < arr.length - 1 ? [br] : ''];
      }, [])
    : text;
};

const roundToNearestInt = (amount, roundTo) => {
  roundTo = parseInt(roundTo); // guarantee integer
  if (roundTo === 0 || isNaN(parseInt(amount))) {
    return 0; // guard against division by zero or undefined amount
  }
  let value = parseInt(amount / roundTo) * roundTo; // round down to nearest multiple of roundTo
  if (amount % roundTo >= roundTo / 2) {
    // if modulo greater than half then
    value += roundTo; // add the roundto
  }
  return value;
};

const setNutrientPercentages = (nutrients) => {
  const { calories, protein, fat, fiber } = nutrients;
  const carbohydrates = nutrients.carbs || nutrients.carbohydrates;
  const processed = { calories, protein, fat, fiber, carbohydrates };
  processed.proteinPercent = calcNutrientPercentage(
    calories,
    protein,
    NUTRIENT_MULTIPLIERS.PROTEIN
  );
  processed.carbohydratesPercent = calcNutrientPercentage(
    calories,
    carbohydrates,
    NUTRIENT_MULTIPLIERS.CARBS
  );
  processed.fatPercent = calcNutrientPercentage(
    calories,
    fat,
    NUTRIENT_MULTIPLIERS.FAT
  );
  return processed;
};

const calculateTolerances = (nutrients, goalNutrients) => {
  const { ...processedNutrients } = nutrients;

  Object.keys(NUTRIENT_TOLERANCES).forEach((key) => {
    const { tolerance, unit, floor } = NUTRIENT_TOLERANCES[key];
    const min =
      unit === '%'
        ? calcMacroToleranceAgainstCalories(
            goalNutrients['calories'],
            tolerance.min,
            NUTRIENT_MULTIPLIERS.FAT
          ).toPrecision(2)
        : goalNutrients[key] - tolerance.min;
    const max =
      unit === '%'
        ? calcMacroToleranceAgainstCalories(
            goalNutrients['calories'],
            tolerance.max,
            NUTRIENT_MULTIPLIERS.FAT
          ).toPrecision(2)
        : goalNutrients[key] + tolerance.max;
    const variance = {
      amount: processedNutrients[key] - goalNutrients[key],
    };

    const toleranceKey = `${key}Variation`;
    const isWithinTolerance =
      key == 'protein' // ignore checking tolerance if is protein
        ? true
        : processedNutrients[key] >= min && processedNutrients[key] <= max;
    processedNutrients[toleranceKey] = {
      key,
      tolerance,
      floor,
      unit,
      isWithinTolerance,
      variance,
      min,
      max,
      goal: goalNutrients[key],
    };

    // should be false if there is no floor defined for the nutrient
    processedNutrients[toleranceKey].minimumReached =
      processedNutrients[key] >= floor || false;
  });
  return processedNutrients;
};

const calcNutrientPercentage = (denominator, nutrient, multiplier) => {
  return (nutrient * multiplier * 100) / denominator;
};

const calcPercentageDifference = (numerator, denominator) => {
  return (numerator / denominator) * 100 - 100;
};

const calcMacroToleranceAgainstCalories = (
  calories,
  percentage,
  multiplier
) => {
  return (calories * percentage) / 100 / multiplier;
};

const parseFloatNumberForDisplay = (number) => {
  return number && Math.round(number);
};

const upperCaseFirstLetter = (string = '') => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const sortObjectListByAccessor = (objectList, accessor) => {
  return objectList.sort((a, b) => {
    const varA = a[accessor].toLowerCase
      ? a[accessor].toLowerCase()
      : a[accessor];
    const varB = b[accessor].toLowerCase
      ? b[accessor].toLowerCase()
      : b[accessor];

    if (varA < varB) return -1;
    if (varA > varB) return 1;
    return 0;
  });
};

const dedupeObjectArray = (objectList, accessor) => {
  const deduped = [];
  objectList.forEach((item) => {
    if (!deduped.find((predicate) => predicate[accessor] === item[accessor])) {
      deduped.push(item);
    }
  });
  return deduped;
};

const joinObjectArrayByAccessor = (objectList, accessor, joiner) => {
  joiner = joiner || ', ';
  return objectList.length > 0
    ? objectList.map((item) => item[accessor]).join(joiner)
    : 'None';
};

// a little function to help us with reordering the result
const reorderArrayObjects = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const cloneObjectArray = (objArray) => {
  return objArray.map((item) => item);
};

const isEmptyObject = (obj) => {
  return typeof obj === 'object' && Object.keys(obj).length === 0;
};

const findObjectInArray = (predicate, objArray, accessor) => {
  return objArray && objArray.find((item) => item[accessor] === predicate);
};

const preprocessData = (originalLst) => {
  // Pre-process data
  return originalLst.map((prop) => {
    return { value: prop.id, label: prop.name || prop.type };
  });
};

const revertProcessedData = (processedLst) => {
  return processedLst.map((prop) => {
    return { id: prop.value, name: prop.label };
  });
};

// Transform for a single day
function transformMealPlanForDay(apiResponse, day) {
  const { nutrients, weeklySelectedIndex, ...meals } = apiResponse;

  const selectedIndexes =
    (weeklySelectedIndex && weeklySelectedIndex[day]) || {};

  const transformedMeals = {};
  const allMealOptions = {};

  DAILY_MEALS.forEach((mealKey) => {
    if (meals[mealKey]) {
      const index = selectedIndexes[mealKey] ?? 0;
      const total = meals[mealKey].length;
      const multiple = total > 1;

      // 1. selected meal for today
      transformedMeals[mealKey] = {
        ...meals[mealKey][index],
        currentMealOptionIndex: index,
        totalMealOptions: total,
        hasMultipleOptions: multiple,
      };

      // 2. all options for this category
      allMealOptions[mealKey] = meals[mealKey].map((meal, i) => ({
        ...meal,
        currentMealOptionIndex: i,
        totalMealOptions: total,
        hasMultipleOptions: multiple,
      }));
    }
  });

  return {
    meals: {
      nutrients,
      ...transformedMeals,
    },
    allMealOptions,
    selectedIndex: selectedIndexes,
  };
}

// Updates a meal option in mealPlans state immutably
function updateMealOption(mealPlans, mealCategory, action, optionIndex) {
  if (!mealPlans || !mealPlans.length) return mealPlans;

  const targetPlanIndex = optionIndex
    ? mealPlans.findIndex((p) => p.option_index === optionIndex)
    : 0;

  if (targetPlanIndex === -1) return mealPlans;

  const plan = mealPlans[targetPlanIndex];
  const options = plan.options[0]; // meals for the day

  const currentMeal = options[mealCategory];
  const allOptions = plan.allMealOptions?.[mealCategory] || [];

  const totalOptions = allOptions.length;

  if (!totalOptions) return mealPlans; // nothing to update

  const currentIndex = currentMeal?.currentMealOptionIndex ?? 0;

  let newIndex = currentIndex;
  if (action === 'next') {
    newIndex = (currentIndex + 1) % totalOptions;
  } else if (action === 'prev') {
    newIndex = (currentIndex - 1 + totalOptions) % totalOptions;
  }

  const selectedOptionMeal = allOptions[newIndex];

  const newMeal = {
    ...selectedOptionMeal,
    category: mealCategory,
    currentMealOptionIndex: newIndex,
    totalMealOptions: totalOptions,
    hasMultipleOptions: totalOptions > 1,
    allMealOptions: plan.allMealOptions,
    pinnedMealTags: currentMeal?.pinnedMealTags || [],
    mealPreferenceMeal: currentMeal?.mealPreferenceMeal || false,
    optionNumber: plan.option_index,
  };

  return mealPlans.map((p, idx) => {
    if (idx !== targetPlanIndex) return p;

    const updatedOptions = [
      {
        ...p.options[0],
        [mealCategory]: newMeal,
      },
    ];

    return Object.assign(Object.create(Object.getPrototypeOf(p)), p, {
      options: updatedOptions,
    });
  });
}

const newAdherenceStartDate = `2023-04-12T00:00:00Z`;

export default {
  NUTRIENT_MULTIPLIERS,
  NUTRIENTS,
  DAILY_MEALS,
  NUTRIENT_TOLERANCES,
  newAdherenceStartDate,
  nl2br,
  roundToNearestInt,
  setNutrientPercentages,
  calcNutrientPercentage,
  calcPercentageDifference,
  calculateTolerances,
  parseFloatNumberForDisplay,
  upperCaseFirstLetter,
  sortObjectListByAccessor,
  dedupeObjectArray,
  joinObjectArrayByAccessor,
  reorderArrayObjects,
  cloneObjectArray,
  isEmptyObject,
  findObjectInArray,
  preprocessData,
  revertProcessedData,
  transformMealPlanForDay,
  updateMealOption,
};
